import React, { useState, useEffect } from "react";
import ImageUploader from "./ImageUploader";
import CategorySelector from "./CategorySelector";
import { Link } from "react-router-dom";

import { getCategories } from "../api/categoryService";
import { createMachine } from "../api/machineService";

import ProductDetailPreview from "./ProductDetailPreview";
import Loading from "./Loading";

import Confetti from "react-confetti";
import { toast, Toaster } from "react-hot-toast";

import CreateListingStepBar from "./CreateListingStepBar";
import AttributeForm from "./AttributeForm";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { useTranslation } from "react-i18next";

const CreateListingPage = () => {
  const { t } = useTranslation();

  const [categories, setCategories] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState({
    id: null,
    selectedCategory: null,
    selectedCategoryName: "",
    selectedSubcategory: null,
    selectedSubcategoryName: "",
    title: "",
    description: "",
    address: "",
    price: "",
    images: [],
    attributes: [],
    year: "",
    brand: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isRequiredAttributesValid, setIsRequiredAttributesValid] =
    useState(false);
  const [attributesValidated, setAttributesValidated] = useState(false);
  const attributeFormRef = React.useRef(null);

  useEffect(() => {
    getCategories()
      .then((data) => {
        setCategories(data);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });
  }, []);

  const handleNextStep = () => {
    console.log("Data:", data);

    // Eğer 3. adımdan 4. adıma geçiyorsak, nitelik doğrulamasını yap
    if (currentStep === 3) {
      setAttributesValidated(true);

      // Alanları kontrol etmeden önce tüm nitelikleri konsolda göster (debugging için)
      console.log("Tüm nitelikler:", data.attributes);

      // Zorunlu alanları kontrol et (niteliklerin mevcut değerlerini göster)
      const requiredAttributes = data.attributes.filter(
        (attr) => attr.required
      );
      console.log("Zorunlu alanlar:", requiredAttributes);

      const missingRequired = requiredAttributes.some((attr) => {
        // Boolean değerler için özel kontrol (false bir değer olarak kabul edilmeli)
        if (attr.value === false) return false;

        // Diğer tüm değer tipleri için boş kontrol
        return !attr.value && attr.value !== 0;
      });

      if (missingRequired) {
        toast.error("Lütfen tüm zorunlu alanları doldurun");
        return; // Önemli: Bu adıma geçişi engeller
      }
    }

    // Diğer adımlarda gerekli alanların kontrolü
    if (currentStep === 2) {
      // Başlık, açıklama, fiyat ve adres için kontrol
      if (!data.title.trim()) {
        toast.error("Lütfen ilan başlığını girin");
        return;
      }

      if (!data.description.trim()) {
        toast.error("Lütfen ilan açıklamasını girin");
        return;
      }
    }

    if (currentStep < 5) setCurrentStep(currentStep + 1);

    // Doğrulama bayrağını sıfırla
    if (currentStep !== 3) {
      setAttributesValidated(false);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const navigateToStep = (step) => {
    if (step <= currentStep) setCurrentStep(step);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Data:", data);

    const formData = new FormData();
    formData.append("name", data.title);
    formData.append("address", data.address);
    formData.append("category", data.selectedCategory?.id);
    formData.append("price", data.price);
    formData.append("description", data.description);
    if (data.currency) {
      formData.append("currency", data.currency);
    }
    data.images.forEach((image) => {
      formData.append("images", image.file);
    });

    data.attributes.forEach((attr) => {
      let slug = `attr__${attr.name}`;
      formData.append(slug, attr.value);
    });

    formData.append("year", data.year);
    formData.append("brand", data.brand);

    // Log form data
    for (var pair of formData.entries()) {
      console.log(pair[0] + ", " + pair[1]);
    }

    try {
      setSubmitting(true);
      const response = await createMachine(formData);
      // Başarı toast’u
      toast.success(t("listingPublished") || "İlan başarıyla yayınlandı");
      console.log("Machine created:", response);
      setData({ ...data, id: response.id });
      setCurrentStep(5);
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        t("listingPublishError") ||
        "İlan yayınlama hatası";
      // Hata toast’u
      toast.error(msg);
      console.error("Error creating machine:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // 1. formatPrice fonksiyonunu ekleyin
  const formatPrice = (value) => {
    const digits = value.replace(/\D/g, "");
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  // Zorunlu alanların geçerliliğini takip eden fonksiyon
  const handleRequiredFieldsValidChange = (isValid) => {
    console.log("Zorunlu alan geçerlilik durumu:", isValid);
    setIsRequiredAttributesValid(isValid);
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="container mx-auto p-6">
        {/* Step-by-Step Process Navigation */}
        <CreateListingStepBar
          currentStep={currentStep}
          navigateToStep={navigateToStep}
        />

        {submitting ? (
          <Loading />
        ) : (
          <div>
            {currentStep === 1 && (
              <div>
                <CategorySelector
                  data={data}
                  categories={categories}
                  setData={setData}
                />
                <button
                  disabled={data?.selectedCategory === null}
                  onClick={handleNextStep}
                  className="btn btn-primary mt-6"
                >
                  {t("next")}
                </button>
              </div>
            )}

            {currentStep === 2 && (
              <div onSubmit={handleNextStep}>
                <h1 className="text-2xl font-bold mb-4">
                  {t("createListingStep2")}
                </h1>
                <div className="mb-4">
                  <label className="block text-gray-700">
                    {t("createListingTitle")}:
                  </label>
                  <input
                    type="text"
                    value={data.title}
                    onChange={(e) =>
                      setData({ ...data, title: e.target.value })
                    }
                    className="input input-bordered w-full"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700">
                    {t("createListingDescription")}:
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={data.description}
                    onChange={(e) => setData({ ...data, description: e })}
                  />
                </div>
                <div className="mb-4 flex w-full">
                  <div className="w-1/2 pr-2">
                    <label className="block text-gray-700">{t("price")}:</label>
                    {/* 2. input'u text yapıp formatPrice ile göster */}
                    <input
                      type="text"
                      value={formatPrice(data.price)}
                      onChange={(e) =>
                        // 3. state’e yalnızca rakamları kaydediyoruz
                        setData({
                          ...data,
                          price: e.target.value.replace(/\D/g, ""),
                        })
                      }
                      className="input input-bordered w-full"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700">&nbsp;</label>
                    <select
                      value={data.currency}
                      onChange={(e) =>
                        setData({ ...data, currency: e.target.value })
                      }
                      className="select select-bordered w-full"
                    >
                      <option value="TRY">Türk Lirası (₺)</option>
                      <option value="USD">Amerikan Doları ($)</option>
                      <option value="EUR">Euro (€)</option>
                      <option value="CNY">Çin Yuanı (¥)</option>
                    </select>
                  </div>
                </div>

                <div className="pr-2">
                  <label className="block text-gray-700">{t("address")}:</label>
                  <textarea
                    value={data.address}
                    onChange={(e) =>
                      setData({ ...data, address: e.target.value })
                    }
                    className="input input-bordered w-full field-sizing-content h-40 resize-none"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700">
                    {t("createListingUploadImages")}:
                  </label>
                  <ImageUploader data={data} setData={setData} />
                </div>
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    className="btn bg-gray-500 text-white hover:bg-gray-600"
                  >
                    Geri
                  </button>
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="btn btn-primary"
                  >
                    Devam
                  </button>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4">Özellikler</h1>
                <div className="mb-4">
                  <AttributeForm
                    ref={attributeFormRef}
                    data={data}
                    setData={setData}
                    selectedCategory={data.selectedCategory}
                    validateRequired={attributesValidated}
                    onRequiredFieldsValidChange={
                      handleRequiredFieldsValidChange
                    }
                  />
                </div>
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    className="btn bg-gray-500 text-white hover:bg-gray-600"
                  >
                    Geri
                  </button>
                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={!isRequiredAttributesValid}
                    className={`btn ${
                      isRequiredAttributesValid
                        ? "btn-primary"
                        : "btn-disabled bg-gray-300"
                    }`}
                  >
                    Devam
                  </button>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div>
                <ProductDetailPreview data={data} />

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    className="btn bg-gray-500 text-white hover:bg-gray-600"
                  >
                    Geri
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-primary"
                  >
                    Yayınla
                  </button>
                </div>
              </div>
            )}

            {currentStep === 5 && (
              <div className="text-center">
                <Confetti />
                <h1 className="text-3xl font-bold text-green-500 mb-4">
                  Tebrikler!
                </h1>
                <p>İlanınız başarıyla yayınlandı.</p>
                {data.id && (
                  <Link
                    to={`/product/${data.id}`}
                    className="btn btn-primary mt-6"
                  >
                    İlana Git
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CreateListingPage;
